/**
 * Taste-based reranking — soft blend of LLM ranking + user taste signals.
 *
 * Design principles (from taste_graph_ranking_principles):
 *  - Reorder, never eliminate — no place is removed
 *  - Soft blend: ~85% LLM position / ~15% taste affinity
 *  - Aggregate by Google place_type, not individual place
 *  - Save > thumbs up (handled by DB weights)
 *  - Mood (vibes) is temporary — taste is persistent background signal
 */

import type { RankedPlace } from '@/hooks/useLLM';

export interface TasteWeights {
  typeWeights: Record<string, number>;
  likeCount: number;
  dislikeCount: number;
  tasteHint: {
    liked: string[];
    disliked: string[];
  };
}

/** Blend ratio: 85% LLM position, 15% taste affinity */
const LLM_WEIGHT = 0.85;
const TASTE_WEIGHT = 0.15;

/**
 * Compute a taste score for a single place based on its Google types
 * and the user's accumulated taste weights.
 */
function placeTasteScore(place: RankedPlace, typeWeights: Record<string, number>): number {
  const types = place.types || [];
  if (types.length === 0) return 0;

  let score = 0;
  let matched = 0;

  for (const t of types) {
    if (t in typeWeights) {
      score += typeWeights[t];
      matched++;
    }
  }

  // Normalise by number of matched types to prevent places with many types
  // from getting inflated scores. Fall back to 0 if no types matched.
  return matched > 0 ? score / matched : 0;
}

/**
 * Apply taste-based reranking to an already-ranked list of places.
 * Returns a new array — does not mutate the input.
 *
 * If tasteWeights is null/empty, returns the input unchanged.
 */
export function applyTasteRerank(
  places: RankedPlace[],
  tasteWeights: TasteWeights | null
): RankedPlace[] {
  if (!tasteWeights || Object.keys(tasteWeights.typeWeights).length === 0) {
    return places;
  }

  const n = places.length;
  if (n <= 1) return places;

  // Compute raw taste scores for all places
  const tasteScores = places.map(p => placeTasteScore(p, tasteWeights.typeWeights));

  // Normalise taste scores to [0, 1] range
  const minTaste = Math.min(...tasteScores);
  const maxTaste = Math.max(...tasteScores);
  const tasteRange = maxTaste - minTaste;

  const scored = places.map((place, i) => {
    // LLM position score: first place gets 1.0, last gets 0.0
    const llmScore = n > 1 ? 1 - i / (n - 1) : 1;

    // Normalised taste score [0, 1]
    const normTaste = tasteRange > 0
      ? (tasteScores[i] - minTaste) / tasteRange
      : 0.5; // If all scores equal, neutral

    const finalScore = LLM_WEIGHT * llmScore + TASTE_WEIGHT * normTaste;

    return { place, finalScore, originalIndex: i };
  });

  // Sort by blended score descending
  scored.sort((a, b) => b.finalScore - a.finalScore);

  return scored.map(s => s.place);
}

/**
 * Build a natural-language taste hint for injection into LLM prompts.
 * Returns null if there's not enough data to be meaningful.
 */
export function buildTasteHintText(tasteHint: { liked: string[]; disliked: string[] } | null): string | null {
  if (!tasteHint) return null;

  const liked = (tasteHint.liked || []).map(formatTypeName);
  const disliked = (tasteHint.disliked || []).map(formatTypeName);

  if (liked.length === 0 && disliked.length === 0) return null;

  const parts: string[] = [];

  if (liked.length > 0) {
    parts.push(`This user particularly enjoys ${liked.join(', ')}`);
  }

  if (disliked.length > 0) {
    parts.push(`They tend to be less interested in ${disliked.join(', ')}`);
  }

  return parts.join('. ') + '.';
}

/**
 * Convert a Google place_type slug to a human-readable name.
 * e.g. "italian_restaurant" → "Italian restaurants"
 */
function formatTypeName(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/s$/, '') + 's';
}
